import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { fabric } from "fabric";

import { ILineOptions } from "fabric/fabric-impl";

import { useAppSelector } from "../../../hooks/reduxHooks";
import { goToDesign } from "../../store/slices/stepSlice";
import { goToSidebarTab } from "../../store/slices/sidebarSlice";
import { setCanvasJson, setSelectObject } from "../../store/slices/viewSlice";
import { APP_CONSTANTS } from "../../../constants/AppConstants";
import { saveCanvasState, getSavedCanvasState, hasAutosavedState } from "../../services/autosave.service";

interface CanvasProps {
  canvasId: string;
  width: string | number;
  height: string | number;
  productImageWidth: any;
  productImageHeight: any;
  productImage: string;
  canvasTop: string | number;
  canvasLeft: string | number;
  design?: any;
}

// past states
const win: any = window;
win.states = null;
win.undo = [];
// reverted states
win.redo = [];
win.save = () => {
  // clear the redo stack
  win.redo = [];

  // initial call won't have a state
  if (win.state) {
    win.undo.push(win.state);
  }
  const canvsId: any = Object.keys(win["design_canvas"])[0];
  win.state = JSON.stringify(
    win["design_canvas"][canvsId].toJSON(APP_CONSTANTS.propToExport)
  );
};
win.replay = (
  playStack: any,
  saveStack: any,
  buttonsOn: any,
  buttonsOff: any
) => {
  saveStack.push(win.state);
  win.state = playStack.pop();
  const canvasId: any = Object.keys(win["design_canvas"])[0];
  win["design_canvas"][canvasId].clear();
  win["design_canvas"][canvasId].loadFromJSON(win.state, function () {
    win["design_canvas"][canvasId].renderAll();
    // now turn the buttons back on if applicable

    if (playStack.length) {
    }
  });
};

const Canvas: React.FC<CanvasProps> = ({
  canvasId,
  width,
  height,
  productImageWidth,
  productImageHeight,
  productImage,
  canvasLeft,
  canvasTop,
  design,
}) => {
  let topIcon = 5;
  const propsToInclude = ["id", "originalImg"];
  const undoStack: any = useRef([]);
  const redoStack: any = useRef([]);
  const viewParent = ".view-editor-area";
  const deleteIcon =
    "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20x%3D%220%22%20y%3D%220%22%20viewBox%3D%220%200%2017%2017%22%20xml%3Aspace%3D%22preserve%22%3E%3Ccircle%20fill%3D%22%23FFF%22%20cx%3D%228.5%22%20cy%3D%228.5%22%20r%3D%228.5%22%3E%3C%2Fcircle%3E%3Cg%20fill%3D%22%234a4a4a%22%3E%3Cpath%20d%3D%22M11.13%204.97l-6.16%206.16c-.25.25-.25.65%200%20.9s.65.25.9%200l6.16-6.16c.25-.25.25-.65%200-.9a.634.634%200%2000-.9%200%22%3E%3C%2Fpath%3E%3Cpath%20d%3D%22M12.03%2011.14L5.86%204.97a.634.634%200%2000-.9%200c-.25.25-.25.65%200%20.9l6.16%206.16c.25.25.65.25.9%200%20.26-.24.26-.65.01-.89%22%3E%3C%2Fpath%3E%3C%2Fg%3E%3C%2Fsvg%3E";
  const scaleIcon =
    "data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2239%22%20height%3D%2239%22%20xml%3Aspace%3D%22preserve%22%20version%3D%221.1%22%3E%3Cg%3E%20%3Ctitle%3ELayer%201%3C%2Ftitle%3E%20%3Ccircle%20stroke%3D%22null%22%20id%3D%22svg_1%22%20r%3D%2219.19439%22%20cy%3D%2219.61106%22%20cx%3D%2219.74994%22%20fill%3D%22%23FFF%22%2F%3E%20%3Cpath%20id%3D%22svg_2%22%20d%3D%22m25.6%2C19.7l0%2C3.8l-10.1%2C-10.1l3.7%2C0l0%2C-3l-8.8%2C0l0%2C8.8l3%2C0l0%2C-3.7l10.1%2C10.1l-3.8%2C0l0%2C3l8.9%2C0l0%2C-8.9l-3%2C0z%22%20fill%3D%22%23039CB5%22%2F%3E%20%3C%2Fg%3E%20%3C%2Fsvg%3E";

  const canvasRef = useRef(null);
  const canvasJson = useAppSelector(
    (state: any) => state.canvases.canvasData[canvasId]?.design
  );
  const selectedViewId = useAppSelector(
    (state: any) => state.canvases.selectedViewId
  );
  const activeObject = useAppSelector(
    (state: any) => state.canvases.canvasData[canvasId]?.activeObject
  );

  const dispatch = useDispatch();

  useEffect(() => {
    let win: any = window;
    if (!win["design_canvas"]) {
      win["design_canvas"] = {};
    }
    win["design_canvas"][canvasId] = new fabric.Canvas(canvasRef.current);
    
    // Check for autosaved state first
    if (hasAutosavedState() && window.location.pathname.includes('start-designing')) {
      const savedState = getSavedCanvasState();
      if (savedState) {
        try {
          win["design_canvas"][canvasId].loadFromJSON(savedState, () => {
            win["design_canvas"][canvasId].renderAll();
            win.save();
            console.log('Restored from autosaved state');
          });
        } catch (error) {
          console.error('Error loading autosaved state:', error);
          // Fall back to the provided design if autosave restore fails
          loadInitialDesign();
        }
      }
    } else {
      // Load the initial design if no autosave exists or if we're not on start-designing
      loadInitialDesign();
    }
    
    function loadInitialDesign() {
      setTimeout(() => {
        if (design) {
          win["design_canvas"][canvasId].loadFromJSON(design.json, () => {
            win["design_canvas"][canvasId].renderAll();
            win.save();
          });
        }
      }, 1000);
    }

    setCustomIcons();
    if (canvasJson) {
      win["design_canvas"][canvasId].loadFromJSON(canvasJson, () => {
        win["design_canvas"][canvasId].renderAll();
      });
    } else {
      dispatch(
        setCanvasJson({
          json: JSON.stringify(
            win["design_canvas"][canvasId].toJSON(APP_CONSTANTS.propToExport)
          ),
          id: canvasId,
        })
      );
    }

    // Set up autosave for all canvas modification events
    const autosaveCanvasState = () => {
      const canvasState = JSON.stringify(
        win["design_canvas"][canvasId].toJSON(APP_CONSTANTS.propToExport)
      );
      saveCanvasState(canvasId, canvasState);
    };

    win["design_canvas"][canvasId].on("object:modified", (e: any) => {
      setControlsVisibility(e.target);
      win.save();
      autosaveCanvasState();
    });
    win["design_canvas"][canvasId].on("selection:created", (e: any) => {
      if (e.selected) {
        for (let index = 0; index < e.selected.length; index++) {
          const element = e.selected[index];
          setControlsVisibility(element);
        }
      }
    });

    win["design_canvas"][canvasId].on("selection:created", (e: any) => {
      if (e.selected[0].text) {
        dispatch(goToDesign());
        dispatch(goToSidebarTab({ tab: "Add Text" }));
      }
      if (e.selected[0].type === "image") {
        dispatch(goToDesign());
        dispatch(goToSidebarTab({ tab: "Upload Image" }));
      }
    });

    win["design_canvas"][canvasId].on("selection:updated", (e: any) => {
      dispatch(
        setSelectObject(e.selected[0].toJSON(APP_CONSTANTS.propToExport))
      );
      if (e.selected[0].text) {
        dispatch(goToDesign());
        dispatch(goToSidebarTab({ tab: "Add Text" }));
      }
      if (e.selected[0].type === "image") {
        dispatch(goToDesign());
        dispatch(goToSidebarTab({ tab: "Upload Image" }));
      }
    });

    win["design_canvas"][canvasId].on("selection:created", (e: any) => {
      dispatch(setSelectObject(e.selected[0]));
    });

    win["design_canvas"][canvasId].on("object:modified", (e: any) => {
      dispatch(setSelectObject(e.target.toJSON(APP_CONSTANTS.propToExport)));
    });
    win["design_canvas"][canvasId].on("mouse:up", () => {
      removeBorder();
      removeGridLine();
    });
    win["design_canvas"][canvasId].on("selection:cleared", () => {
      setTimeout(() => {
        dispatch(setSelectObject(null));
      }, 100);
    });
    win["design_canvas"][canvasId].on("object:moving", (e: any) => {
      setSnapGridLines(e);
    });

    win["design_canvas"][canvasId].on("object:added", () => {
      autosaveCanvasState();
    });
    
    win["design_canvas"][canvasId].on("object:removed", () => {
      autosaveCanvasState();
    });

    setResponsiveEditor();
    window.addEventListener(
      "resize",
      (event) => {
        setResponsiveEditor();
      },
      true
    );
    setSnapGrid();
    setBoundingRect();

    return () => {
      win["design_canvas"][canvasId].dispose();
    };
  }, []);

  const handleDelete = () => {
    if (activeObject) {
      activeObject.remove();
      // dispatch(setActiveObject(null));
    }
  };

  const setSnapGridLines = (options: any) => {
    var snapZone = 15;
    let win: any = window;
    var objectMiddle = options.target.left;
    if (
      objectMiddle > win["design_canvas"][canvasId].width / 2 - snapZone &&
      objectMiddle < win["design_canvas"][canvasId].width / 2 + snapZone
    ) {
      options.target
        .set({
          left: win["design_canvas"][canvasId].width / 2,
        })
        .setCoords();
    }
    addBorder();
    setGridLine();
  };

  const setSnapGrid = () => {
    let win: any = window;
    let line9: any = new fabric.Line(
      [
        win["design_canvas"][canvasId].width / 2,
        0,
        win["design_canvas"][canvasId].width / 2,
        win["design_canvas"][canvasId].height,
      ],
      {
        stroke: "#ccc",
        id: "gridLine",
        opacity: 0,
      } as ILineOptions
    );
    line9.set("id", "gridLine");
    line9.selectable = false;
    line9.evented = false;
    win["design_canvas"][canvasId].add(line9);
  };
  const addBorder = () => {
    document
      .getElementsByClassName("canvas-container")[0]
      .classList.add("canvas-border");
  };
  const removeBorder = () => {
    document
      .getElementsByClassName("canvas-container")[0]
      .classList.remove("canvas-border");
  };
  const setGridLine = () => {
    let win: any = window;
    const lineObjects = getObjectByType("line");

    let object = lineObjects ? lineObjects[0] : null;
    if (object) object.set("opacity", 1);
    win["design_canvas"][canvasId].renderAll();
  };
  const getObjectByType = (type: any) => {
    let win: any = window;
    if (!type) return null;
    let filteredObjects = [];
    let canvasObjects = win["design_canvas"][canvasId]._objects;
    for (let index = 0; index < canvasObjects.length; index++) {
      const object = canvasObjects[index];
      if (object.type == type) filteredObjects.push(object);
    }
    return filteredObjects;
  };
  const removeGridLine = () => {
    let win: any = window;
    const lineObjects = getObjectByType("line");

    let object = lineObjects ? lineObjects[0] : null;
    if (object) object.set("opacity", 0);
    win["design_canvas"][canvasId].renderAll();
  };
  const setResponsiveEditor = async () => {
    const parent: any = document.querySelector(viewParent);
    const canvasContainer: any = document.getElementById(
      "product-container-id-" + canvasId
    );
    if (!productImageWidth) {
      const img: any = await getMeta(productImage);
      productImageWidth = img.naturalWidth;
      productImageHeight = img.naturalHeight;
    }

    const width = parent?.offsetWidth;
    const height = parent?.offsetHeight;
    if (!height) return;
    if (!width) return;
    // console.log(width, height);
    //if (viewDetails.product_image_width > width || viewDetails.product_image_height > height) {
    let wsf = width / parseFloat(productImageWidth);
    let hsf = height / parseFloat(productImageHeight);
    let finalSF = 1;
    if (wsf > hsf) {
      finalSF = hsf;
    } else {
      finalSF = wsf;
    }
    if (finalSF > 1) {
      finalSF = 1;
    }
    canvasContainer.style.transform = "scale(" + finalSF + ", " + finalSF + ")";
    let scaledH = productImageHeight * finalSF;
    let scaledW = productImageWidth * finalSF;
    let right = (width - productImageWidth) / 2;

    let top =
      window.innerWidth < 700
        ? -window.innerHeight / 4
        : (height - productImageHeight) / 2;
    canvasContainer.style.position = "relative"; // ex-absolute ???
    canvasContainer.style.left = right + "px"; //ex-style.left ???
    canvasContainer.style.top = top + "px";
    // canvasContainer.style.padding = "80px 0 0";
    // canvasContainer.style.width = '50vw';
  };

  const getMeta = async (url: any) => {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  };
  const setBoundingRect = () => {
    let win: any = window;
    const canvas = win["design_canvas"][canvasId];
    let self = this;
    var left1 = 0;
    var top1 = 0;
    var scale1x = 0;
    var scale1y = 0;
    var width1 = 0;
    var height1 = 0;

    canvas.on("object:moving", function (e: any) {
      const win: any = window;
      if (!win.designHistory) {
        win.designHistory = [];
      }
      win.designHistory.push(
        JSON.stringify(canvas.toJSON(APP_CONSTANTS.propToExport))
      );
    });
    canvas.on("object:removed", function (e: any) {
      const win: any = window;
      if (!win.designHistory) {
        win.designHistory = [];
      }
      win.designHistory.push(
        JSON.stringify(canvas.toJSON(APP_CONSTANTS.propToExport))
      );
    });
    canvas.on("object:added", function (e: any) {
      const win: any = window;
      if (!win.designHistory) {
        win.designHistory = [];
      }
      win.designHistory.push(
        JSON.stringify(canvas.toJSON(APP_CONSTANTS.propToExport))
      );
    });
    canvas.on("object:resizing", function (e: any) {
      const win: any = window;
      if (!win.designHistory) {
        win.designHistory = [];
      }
      win.designHistory.push(
        JSON.stringify(canvas.toJSON(APP_CONSTANTS.propToExport))
      );
      var obj = e.target;
      // if object is too big ignore
      if (
        obj.currentHeight > obj.canvas.height ||
        obj.currentWidth > obj.canvas.width
      ) {
        return;
      }
      obj.setCoords();
      // top-left  corner
      if (obj.getBoundingRect().top < 0 || obj.getBoundingRect().left < 0) {
        obj.top = Math.max(obj.top, obj.top - obj.getBoundingRect().top);
        obj.left = Math.max(obj.left, obj.left - obj.getBoundingRect().left);
      }
      // bot-right corner
      if (
        obj.getBoundingRect().top + obj.getBoundingRect().height >
          obj.canvas.height ||
        obj.getBoundingRect().left + obj.getBoundingRect().width >
          obj.canvas.width
      ) {
        obj.top = Math.min(
          obj.top,
          obj.canvas.height -
            obj.getBoundingRect().height +
            obj.top -
            obj.getBoundingRect().top
        );
        obj.left = Math.min(
          obj.left,
          obj.canvas.width -
            obj.getBoundingRect().width +
            obj.left -
            obj.getBoundingRect().left
        );
      }
    });
    canvas.on("object:modified", function (e: any) {
      debugger;
      const win: any = window;
      if (!win.designHistory) {
        win.designHistory = [];
      }
      win.designHistory.push(
        JSON.stringify(canvas.toJSON(APP_CONSTANTS.propToExport))
      );
    });
    canvas.on("object:scaling", function (e: any) {
      const win: any = window;
      if (!win.designHistory) {
        win.designHistory = [];
      }
      win.designHistory.push(
        JSON.stringify(canvas.toJSON(APP_CONSTANTS.propToExport))
      );
      var obj = e.target;
      if (obj.type === "text") {
        obj.fontSize = Math.ceil(obj.scaleX * obj.fontSize);
        obj.scaleX = 1;
        obj.scaleY = 1;
        obj.setCoords();
      }

      var brNew = obj.getBoundingRect();

      if (
        brNew.width + brNew.left >= obj.canvas.width ||
        brNew.height + brNew.top >= obj.canvas.height ||
        brNew.left < 0 ||
        brNew.top < 0
      ) {
        obj.left = left1;
        obj.top = top1;
        obj.scaleX = scale1x;
        obj.scaleY = scale1y;
        obj.width = width1;
        obj.height = height1;
      } else {
        left1 = obj.left;
        top1 = obj.top;
        scale1x = obj.scaleX;
        scale1y = obj.scaleY;
        width1 = obj.width;
        height1 = obj.height;
      }
      // self.sendCustomEvent(
      //   e.target,
      //   "selection:scaling",
      //   e.target.type
      // );
      addBorder();
      setGridLine();
    });
  };

  /**
   * Reference: https://github.com/fabricjs/fabric.js/blob/master/src/mixins/default_controls.js
   * http://fabricjs.com/custom-control-render
   */
  const setCustomIcons = () => {
    const deleteIcon =
      "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20x%3D%220%22%20y%3D%220%22%20viewBox%3D%220%200%2017%2017%22%20xml%3Aspace%3D%22preserve%22%3E%3Ccircle%20fill%3D%22%23FFF%22%20cx%3D%228.5%22%20cy%3D%228.5%22%20r%3D%228.5%22%3E%3C%2Fcircle%3E%3Cg%20fill%3D%22%234a4a4a%22%3E%3Cpath%20d%3D%22M11.13%204.97l-6.16%206.16c-.25.25-.25.65%200%20.9s.65.25.9%200l6.16-6.16c.25-.25.25-.65%200-.9a.634.634%200%2000-.9%200%22%3E%3C%2Fpath%3E%3Cpath%20d%3D%22M12.03%2011.14L5.86%204.97a.634.634%200%2000-.9%200c-.25.25-.25.65%200%20.9l6.16%206.16c.25.25.65.25.9%200%20.26-.24.26-.65.01-.89%22%3E%3C%2Fpath%3E%3C%2Fg%3E%3C%2Fsvg%3E";
    const scaleIcon =
      "data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2239%22%20height%3D%2239%22%20xml%3Aspace%3D%22preserve%22%20version%3D%221.1%22%3E%3Cg%3E%20%3Ctitle%3ELayer%201%3C%2Ftitle%3E%20%3Ccircle%20stroke%3D%22null%22%20id%3D%22svg_1%22%20r%3D%2219.19439%22%20cy%3D%2219.61106%22%20cx%3D%2219.74994%22%20fill%3D%22%23FFF%22%2F%3E%20%3Cpath%20id%3D%22svg_2%22%20d%3D%22m25.6%2C19.7l0%2C3.8l-10.1%2C-10.1l3.7%2C0l0%2C-3l-8.8%2C0l0%2C8.8l3%2C0l0%2C-3.7l10.1%2C10.1l-3.8%2C0l0%2C3l8.9%2C0l0%2C-8.9l-3%2C0z%22%20fill%3D%22%23039CB5%22%2F%3E%20%3C%2Fg%3E%20%3C%2Fsvg%3E";

    var deleteImg = document.createElement("img");
    deleteImg.src = deleteIcon;

    var scaleImg = document.createElement("img");
    scaleImg.src = scaleIcon;

    fabric.Object.prototype.transparentCorners = true;
    fabric.Object.prototype.cornerSize = 20;
    fabric.Object.prototype.cornerStyle = "circle";

    let fab: any = fabric;
    var controlsUtils = fab.controlsUtils;

    fabric.Object.prototype.controls.deleteControl = new fabric.Control({
      x: -0.5,
      y: -0.5,
      offsetY: 5,
      offsetX: 5,

      cursorStyle: "pointer",
      mouseUpHandler: deleteObject,
      render: renderIcon(deleteImg),
      cornerSize: 20,
    } as any);
    fabric.Object.prototype.controls.scaleIcon = new fabric.Control({
      offsetY: -5,
      offsetX: -5,
      x: 0.5,
      y: 0.5,

      cursorStyle: "nw-resize",
      actionHandler: controlsUtils.scalingEqually,
      render: renderIcon(scaleImg),
      // cornerSize: 40,
    });
  };

  /**
   * Render Icon the time of selection
   * @param {Striong} icon
   * @returns
   */
  const renderIcon = (icon: any) => {
    return function renderIcon(
      ctx: any,
      left: any,
      top: any,
      styleOverride: any,
      fabricObject: any
    ) {
      var size = 25;
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
      ctx.drawImage(icon, -size / 2, -size / 2, size, size);
      ctx.restore();
    };
  };
  const setControlsVisibility = (object: any) => {
    object.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false,
      bl: false,
      br: false,
      tl: false,
      tr: false,
      mtr: false,
    });
  };

  const deleteObject = (eventData: any, transform: any) => {
    var target = transform.target;
    var canvas = target.canvas;
    canvas.remove(target);
    //self.stateManager.saveState();
    canvas.requestRenderAll();
  };
  return (
    <div
      className="product-container"
      id={"product-container-id-" + canvasId}
      style={{
        display: selectedViewId !== canvasId ? "none" : "block",
        width: productImageWidth,
        height: productImageHeight,
      }}
    >
      <div>
        <img
          alt=""
          id={"product-image-" + canvasId}
          src={productImage}
          width={productImageWidth}
          height={productImageHeight}
          crossOrigin="anonymous"
        />
        <div
          style={{
            position: "absolute",
            left: canvasLeft + "px",
            top: canvasTop + "px",
          }}
        >
          <canvas ref={canvasRef} width={width} height={height} id={canvasId} />
        </div>
      </div>
    </div>
  );
};

export default Canvas;
